// Hand-authored stroke templates for the letter-tracing game.
// Coordinates are normalised to a 1x1 box (y down). Cap height 0.12..0.88,
// x-height 0.42..0.88, descenders to 0.98. Stroke order and direction follow
// standard US kindergarten formation (top-to-bottom, left-to-right; O and C
// start at the top and go counter-clockwise; S is one stroke from the top right).
// Path commands are limited to M, L, C, Q and A. `start` is each stroke's first point.

export const GLYPH_PROMPT_VOCAB = Object.freeze([
  "Big curve around",
  "Big line down",
  "Big line down and hook",
  "Bump",
  "Curve all the way around",
  "Curve around",
  "Curve around, big line down",
  "Curve around, curve around",
  "Curve around, curve back",
  "Curve around, slide down, line across",
  "Curve down and around",
  "Curve, then big line down",
  "Dot on top",
  "Line across",
  "Line across, slide down",
  "Line across, slide down, line across",
  "Line down",
  "Line down and hook",
  "Line down, curve around, and up",
  "Little curve",
  "Little line across",
  "Little line across, curve around",
  "Little line down",
  "Little line down, curve around",
  "Little line down, line across",
  "Little slide down",
  "Make an S, then curve back up",
  "Slide down to the left",
  "Slide down to the right",
  "Slide down, slide up",
  "Slide down, slide up, slide down, slide up",
  "Slide up",
]);

export const GLYPH_STROKES = Object.freeze({
  "A": {
    hint: "Big A: slide down, slide down, little line across the middle.",
    strokes: [
      {
        d: "M 0.5,0.12 L 0.24,0.88",
        start: [0.5, 0.12],
        prompt: "Slide down to the left",
      },
      {
        d: "M 0.5,0.12 L 0.76,0.88",
        start: [0.5, 0.12],
        prompt: "Slide down to the right",
      },
      {
        d: "M 0.335,0.62 L 0.665,0.62",
        start: [0.335, 0.62],
        prompt: "Little line across",
      },
    ],
  },
  "B": {
    hint: "Big B: big line down, then two bumps on the right.",
    strokes: [
      {
        d: "M 0.28,0.12 L 0.28,0.88",
        start: [0.28, 0.12],
        prompt: "Big line down",
      },
      {
        d: "M 0.28,0.12 C 0.7,0.12 0.7,0.5 0.28,0.5",
        start: [0.28, 0.12],
        prompt: "Bump",
      },
      {
        d: "M 0.28,0.5 C 0.78,0.5 0.78,0.88 0.28,0.88",
        start: [0.28, 0.5],
        prompt: "Bump",
      },
    ],
  },
  "C": {
    hint: "Big C: start at the top and curve around like a cup on its side.",
    strokes: [
      {
        d: "M 0.686,0.209 A 0.29 0.38 0 1 0 0.686,0.791",
        start: [0.686, 0.209],
        prompt: "Curve around",
      },
    ],
  },
  "D": {
    hint: "Big D: big line down, then a big curve around.",
    strokes: [
      {
        d: "M 0.28,0.12 L 0.28,0.88",
        start: [0.28, 0.12],
        prompt: "Big line down",
      },
      {
        d: "M 0.28,0.12 C 0.86,0.12 0.86,0.88 0.28,0.88",
        start: [0.28, 0.12],
        prompt: "Big curve around",
      },
    ],
  },
  "E": {
    hint: "Big E: big line down, then three lines across.",
    strokes: [
      {
        d: "M 0.28,0.12 L 0.28,0.88",
        start: [0.28, 0.12],
        prompt: "Big line down",
      },
      {
        d: "M 0.28,0.12 L 0.72,0.12",
        start: [0.28, 0.12],
        prompt: "Line across",
      },
      {
        d: "M 0.28,0.5 L 0.64,0.5",
        start: [0.28, 0.5],
        prompt: "Little line across",
      },
      {
        d: "M 0.28,0.88 L 0.72,0.88",
        start: [0.28, 0.88],
        prompt: "Line across",
      },
    ],
  },
  "F": {
    hint: "Big F: big line down, line across the top, little line across the middle.",
    strokes: [
      {
        d: "M 0.28,0.12 L 0.28,0.88",
        start: [0.28, 0.12],
        prompt: "Big line down",
      },
      {
        d: "M 0.28,0.12 L 0.72,0.12",
        start: [0.28, 0.12],
        prompt: "Line across",
      },
      {
        d: "M 0.28,0.5 L 0.64,0.5",
        start: [0.28, 0.5],
        prompt: "Little line across",
      },
    ],
  },
  "G": {
    hint: "Big G: curve around like a C, then a little line across into the middle.",
    strokes: [
      {
        d: "M 0.686,0.209 A 0.29 0.38 0 1 0 0.79,0.5",
        start: [0.686, 0.209],
        prompt: "Curve around",
      },
      {
        d: "M 0.54,0.5 L 0.79,0.5",
        start: [0.54, 0.5],
        prompt: "Little line across",
      },
    ],
  },
  "H": {
    hint: "Big H: two big lines down, then a line across the middle.",
    strokes: [
      {
        d: "M 0.26,0.12 L 0.26,0.88",
        start: [0.26, 0.12],
        prompt: "Big line down",
      },
      {
        d: "M 0.74,0.12 L 0.74,0.88",
        start: [0.74, 0.12],
        prompt: "Big line down",
      },
      {
        d: "M 0.26,0.5 L 0.74,0.5",
        start: [0.26, 0.5],
        prompt: "Line across",
      },
    ],
  },
  "I": {
    hint: "Big I: big line down, little line across the top, little line across the bottom.",
    strokes: [
      {
        d: "M 0.5,0.12 L 0.5,0.88",
        start: [0.5, 0.12],
        prompt: "Big line down",
      },
      {
        d: "M 0.32,0.12 L 0.68,0.12",
        start: [0.32, 0.12],
        prompt: "Little line across",
      },
      {
        d: "M 0.32,0.88 L 0.68,0.88",
        start: [0.32, 0.88],
        prompt: "Little line across",
      },
    ],
  },
  "J": {
    hint: "Big J: big line down and hook at the bottom.",
    strokes: [
      {
        d: "M 0.62,0.12 L 0.62,0.64 C 0.62,0.82 0.5,0.9 0.4,0.87 C 0.32,0.85 0.28,0.78 0.28,0.7",
        start: [0.62, 0.12],
        prompt: "Big line down and hook",
      },
    ],
  },
  "K": {
    hint: "Big K: big line down, slide in to the middle, slide out to the bottom.",
    strokes: [
      {
        d: "M 0.28,0.12 L 0.28,0.88",
        start: [0.28, 0.12],
        prompt: "Big line down",
      },
      {
        d: "M 0.72,0.12 L 0.28,0.52",
        start: [0.72, 0.12],
        prompt: "Slide down to the left",
      },
      {
        d: "M 0.4,0.42 L 0.74,0.88",
        start: [0.4, 0.42],
        prompt: "Slide down to the right",
      },
    ],
  },
  "L": {
    hint: "Big L: big line down, then a line across the bottom.",
    strokes: [
      {
        d: "M 0.3,0.12 L 0.3,0.88",
        start: [0.3, 0.12],
        prompt: "Big line down",
      },
      {
        d: "M 0.3,0.88 L 0.72,0.88",
        start: [0.3, 0.88],
        prompt: "Line across",
      },
    ],
  },
  "M": {
    hint: "Big M: big line down, slide down, slide up, big line down.",
    strokes: [
      {
        d: "M 0.2,0.12 L 0.2,0.88",
        start: [0.2, 0.12],
        prompt: "Big line down",
      },
      {
        d: "M 0.2,0.12 L 0.5,0.8",
        start: [0.2, 0.12],
        prompt: "Slide down to the right",
      },
      {
        d: "M 0.5,0.8 L 0.8,0.12",
        start: [0.5, 0.8],
        prompt: "Slide up",
      },
      {
        d: "M 0.8,0.12 L 0.8,0.88",
        start: [0.8, 0.12],
        prompt: "Big line down",
      },
    ],
  },
  "N": {
    hint: "Big N: big line down, slide down, big line down.",
    strokes: [
      {
        d: "M 0.24,0.12 L 0.24,0.88",
        start: [0.24, 0.12],
        prompt: "Big line down",
      },
      {
        d: "M 0.24,0.12 L 0.76,0.88",
        start: [0.24, 0.12],
        prompt: "Slide down to the right",
      },
      {
        d: "M 0.76,0.12 L 0.76,0.88",
        start: [0.76, 0.12],
        prompt: "Big line down",
      },
    ],
  },
  "O": {
    hint: "Big O: start at the top and curve all the way around.",
    strokes: [
      {
        d: "M 0.397,0.143 A 0.3 0.38 0 1 0 0.693,0.791 A 0.3 0.38 0 0 0 0.397,0.143",
        start: [0.397, 0.143],
        prompt: "Curve all the way around",
      },
    ],
  },
  "P": {
    hint: "Big P: big line down, then a bump at the top.",
    strokes: [
      {
        d: "M 0.28,0.12 L 0.28,0.88",
        start: [0.28, 0.12],
        prompt: "Big line down",
      },
      {
        d: "M 0.28,0.12 C 0.76,0.12 0.76,0.54 0.28,0.54",
        start: [0.28, 0.12],
        prompt: "Bump",
      },
    ],
  },
  "Q": {
    hint: "Big Q: curve all the way around like an O, then a little slide for the tail.",
    strokes: [
      {
        d: "M 0.397,0.143 A 0.3 0.38 0 1 0 0.693,0.791 A 0.3 0.38 0 0 0 0.397,0.143",
        start: [0.397, 0.143],
        prompt: "Curve all the way around",
      },
      {
        d: "M 0.58,0.68 L 0.8,0.9",
        start: [0.58, 0.68],
        prompt: "Little slide down",
      },
    ],
  },
  "R": {
    hint: "Big R: big line down, bump at the top, then slide down.",
    strokes: [
      {
        d: "M 0.28,0.12 L 0.28,0.88",
        start: [0.28, 0.12],
        prompt: "Big line down",
      },
      {
        d: "M 0.28,0.12 C 0.74,0.12 0.74,0.52 0.28,0.52",
        start: [0.28, 0.12],
        prompt: "Bump",
      },
      {
        d: "M 0.28,0.52 L 0.74,0.88",
        start: [0.28, 0.52],
        prompt: "Slide down to the right",
      },
    ],
  },
  "S": {
    hint: "Big S: start at the top, curve one way, then curve the other way.",
    strokes: [
      {
        d: "M 0.691,0.215 A 0.22 0.19 0 1 0 0.5,0.5 A 0.22 0.19 0 1 1 0.309,0.785",
        start: [0.691, 0.215],
        prompt: "Curve around, curve back",
      },
    ],
  },
  "T": {
    hint: "Big T: big line down, then a line across the top.",
    strokes: [
      {
        d: "M 0.5,0.12 L 0.5,0.88",
        start: [0.5, 0.12],
        prompt: "Big line down",
      },
      {
        d: "M 0.24,0.12 L 0.76,0.12",
        start: [0.24, 0.12],
        prompt: "Line across",
      },
    ],
  },
  "U": {
    hint: "Big U: big line down, curve around the bottom, and back up.",
    strokes: [
      {
        d: "M 0.24,0.12 L 0.24,0.62 A 0.26 0.26 0 0 0 0.76,0.62 L 0.76,0.12",
        start: [0.24, 0.12],
        prompt: "Line down, curve around, and up",
      },
    ],
  },
  "V": {
    hint: "Big V: slide down, then slide up.",
    strokes: [
      {
        d: "M 0.22,0.12 L 0.5,0.88 L 0.78,0.12",
        start: [0.22, 0.12],
        prompt: "Slide down, slide up",
      },
    ],
  },
  "W": {
    hint: "Big W: slide down, slide up, slide down, slide up.",
    strokes: [
      {
        d: "M 0.14,0.12 L 0.32,0.88 L 0.5,0.32 L 0.68,0.88 L 0.86,0.12",
        start: [0.14, 0.12],
        prompt: "Slide down, slide up, slide down, slide up",
      },
    ],
  },
  "X": {
    hint: "Big X: slide down one way, then slide down the other way.",
    strokes: [
      {
        d: "M 0.24,0.12 L 0.76,0.88",
        start: [0.24, 0.12],
        prompt: "Slide down to the right",
      },
      {
        d: "M 0.76,0.12 L 0.24,0.88",
        start: [0.76, 0.12],
        prompt: "Slide down to the left",
      },
    ],
  },
  "Y": {
    hint: "Big Y: two slides down to the middle, then a little line down.",
    strokes: [
      {
        d: "M 0.22,0.12 L 0.5,0.52",
        start: [0.22, 0.12],
        prompt: "Slide down to the right",
      },
      {
        d: "M 0.78,0.12 L 0.5,0.52",
        start: [0.78, 0.12],
        prompt: "Slide down to the left",
      },
      {
        d: "M 0.5,0.52 L 0.5,0.88",
        start: [0.5, 0.52],
        prompt: "Little line down",
      },
    ],
  },
  "Z": {
    hint: "Big Z: line across, slide down, line across.",
    strokes: [
      {
        d: "M 0.24,0.12 L 0.76,0.12 L 0.24,0.88 L 0.76,0.88",
        start: [0.24, 0.12],
        prompt: "Line across, slide down, line across",
      },
    ],
  },
  "a": {
    hint: "Little a: curve around like a c, then a little line down on the right.",
    strokes: [
      {
        d: "M 0.68,0.5 C 0.62,0.4 0.28,0.36 0.28,0.65 C 0.28,0.94 0.62,0.9 0.68,0.8",
        start: [0.68, 0.5],
        prompt: "Curve around",
      },
      {
        d: "M 0.68,0.42 L 0.68,0.88",
        start: [0.68, 0.42],
        prompt: "Little line down",
      },
    ],
  },
  "b": {
    hint: "Little b: big line down, then a bump.",
    strokes: [
      {
        d: "M 0.3,0.12 L 0.3,0.88",
        start: [0.3, 0.12],
        prompt: "Big line down",
      },
      {
        d: "M 0.3,0.5 C 0.36,0.4 0.7,0.36 0.7,0.65 C 0.7,0.94 0.36,0.9 0.3,0.8",
        start: [0.3, 0.5],
        prompt: "Bump",
      },
    ],
  },
  "c": {
    hint: "Little c: start at the top and curve around.",
    strokes: [
      {
        d: "M 0.638,0.487 A 0.21 0.23 0 1 0 0.638,0.813",
        start: [0.638, 0.487],
        prompt: "Curve around",
      },
    ],
  },
  "d": {
    hint: "Little d: curve around like a c, then a big line down.",
    strokes: [
      {
        d: "M 0.68,0.5 C 0.62,0.4 0.28,0.36 0.28,0.65 C 0.28,0.94 0.62,0.9 0.68,0.8",
        start: [0.68, 0.5],
        prompt: "Curve around",
      },
      {
        d: "M 0.68,0.12 L 0.68,0.88",
        start: [0.68, 0.12],
        prompt: "Big line down",
      },
    ],
  },
  "e": {
    hint: "Little e: little line across, then curve up and around.",
    strokes: [
      {
        d: "M 0.3,0.65 L 0.69,0.65 C 0.69,0.36 0.3,0.34 0.3,0.65 C 0.3,0.92 0.6,0.9 0.69,0.8",
        start: [0.3, 0.65],
        prompt: "Little line across, curve around",
      },
    ],
  },
  "f": {
    hint: "Little f: curve at the top, big line down, then a little line across.",
    strokes: [
      {
        d: "M 0.66,0.2 C 0.64,0.09 0.4,0.08 0.4,0.26 L 0.4,0.88",
        start: [0.66, 0.2],
        prompt: "Curve, then big line down",
      },
      {
        d: "M 0.26,0.42 L 0.56,0.42",
        start: [0.26, 0.42],
        prompt: "Little line across",
      },
    ],
  },
  "g": {
    hint: "Little g: curve around like a c, then line down and hook.",
    strokes: [
      {
        d: "M 0.68,0.5 C 0.62,0.4 0.28,0.36 0.28,0.65 C 0.28,0.94 0.62,0.9 0.68,0.8",
        start: [0.68, 0.5],
        prompt: "Curve around",
      },
      {
        d: "M 0.68,0.42 L 0.68,0.84 C 0.68,0.96 0.54,1 0.4,0.94",
        start: [0.68, 0.42],
        prompt: "Line down and hook",
      },
    ],
  },
  "h": {
    hint: "Little h: big line down, then a bump.",
    strokes: [
      {
        d: "M 0.3,0.12 L 0.3,0.88",
        start: [0.3, 0.12],
        prompt: "Big line down",
      },
      {
        d: "M 0.3,0.6 C 0.34,0.38 0.7,0.36 0.7,0.62 L 0.7,0.88",
        start: [0.3, 0.6],
        prompt: "Bump",
      },
    ],
  },
  "i": {
    hint: "Little i: little line down, then a dot on top.",
    strokes: [
      {
        d: "M 0.5,0.42 L 0.5,0.88",
        start: [0.5, 0.42],
        prompt: "Little line down",
      },
      {
        d: "M 0.5,0.242 A 0.028 0.028 0 1 1 0.49,0.296 A 0.028 0.028 0 0 1 0.5,0.242",
        start: [0.5, 0.242],
        prompt: "Dot on top",
      },
    ],
  },
  "j": {
    hint: "Little j: line down and hook, then a dot on top.",
    strokes: [
      {
        d: "M 0.56,0.42 L 0.56,0.84 C 0.56,0.96 0.44,1 0.32,0.94",
        start: [0.56, 0.42],
        prompt: "Line down and hook",
      },
      {
        d: "M 0.56,0.242 A 0.028 0.028 0 1 1 0.55,0.296 A 0.028 0.028 0 0 1 0.56,0.242",
        start: [0.56, 0.242],
        prompt: "Dot on top",
      },
    ],
  },
  "k": {
    hint: "Little k: big line down, slide in, slide out.",
    strokes: [
      {
        d: "M 0.3,0.12 L 0.3,0.88",
        start: [0.3, 0.12],
        prompt: "Big line down",
      },
      {
        d: "M 0.68,0.42 L 0.3,0.66",
        start: [0.68, 0.42],
        prompt: "Slide down to the left",
      },
      {
        d: "M 0.42,0.59 L 0.7,0.88",
        start: [0.42, 0.59],
        prompt: "Slide down to the right",
      },
    ],
  },
  "l": {
    hint: "Little l: one big line down.",
    strokes: [
      {
        d: "M 0.5,0.12 L 0.5,0.88",
        start: [0.5, 0.12],
        prompt: "Big line down",
      },
    ],
  },
  "m": {
    hint: "Little m: little line down, bump, bump.",
    strokes: [
      {
        d: "M 0.2,0.42 L 0.2,0.88",
        start: [0.2, 0.42],
        prompt: "Little line down",
      },
      {
        d: "M 0.2,0.58 C 0.22,0.38 0.5,0.38 0.5,0.6 L 0.5,0.88",
        start: [0.2, 0.58],
        prompt: "Bump",
      },
      {
        d: "M 0.5,0.58 C 0.52,0.38 0.8,0.38 0.8,0.6 L 0.8,0.88",
        start: [0.5, 0.58],
        prompt: "Bump",
      },
    ],
  },
  "n": {
    hint: "Little n: little line down, then a bump.",
    strokes: [
      {
        d: "M 0.3,0.42 L 0.3,0.88",
        start: [0.3, 0.42],
        prompt: "Little line down",
      },
      {
        d: "M 0.3,0.6 C 0.32,0.38 0.7,0.36 0.7,0.62 L 0.7,0.88",
        start: [0.3, 0.6],
        prompt: "Bump",
      },
    ],
  },
  "o": {
    hint: "Little o: start at the top and curve all the way around.",
    strokes: [
      {
        d: "M 0.428,0.434 A 0.21 0.23 0 1 0 0.635,0.826 A 0.21 0.23 0 0 0 0.428,0.434",
        start: [0.428, 0.434],
        prompt: "Curve all the way around",
      },
    ],
  },
  "p": {
    hint: "Little p: line down under the line, then a bump.",
    strokes: [
      {
        d: "M 0.3,0.42 L 0.3,0.98",
        start: [0.3, 0.42],
        prompt: "Line down",
      },
      {
        d: "M 0.3,0.5 C 0.36,0.4 0.7,0.36 0.7,0.65 C 0.7,0.94 0.36,0.9 0.3,0.8",
        start: [0.3, 0.5],
        prompt: "Bump",
      },
    ],
  },
  "q": {
    hint: "Little q: curve around like a c, then line down under the line.",
    strokes: [
      {
        d: "M 0.68,0.5 C 0.62,0.4 0.28,0.36 0.28,0.65 C 0.28,0.94 0.62,0.9 0.68,0.8",
        start: [0.68, 0.5],
        prompt: "Curve around",
      },
      {
        d: "M 0.68,0.42 L 0.68,0.98",
        start: [0.68, 0.42],
        prompt: "Line down",
      },
    ],
  },
  "r": {
    hint: "Little r: little line down, then a little curve.",
    strokes: [
      {
        d: "M 0.32,0.42 L 0.32,0.88",
        start: [0.32, 0.42],
        prompt: "Little line down",
      },
      {
        d: "M 0.32,0.6 C 0.34,0.44 0.52,0.4 0.66,0.48",
        start: [0.32, 0.6],
        prompt: "Little curve",
      },
    ],
  },
  "s": {
    hint: "Little s: start at the top, curve one way, then curve the other way.",
    strokes: [
      {
        d: "M 0.647,0.478 A 0.17 0.115 0 1 0 0.5,0.65 A 0.17 0.115 0 1 1 0.353,0.823",
        start: [0.647, 0.478],
        prompt: "Curve around, curve back",
      },
    ],
  },
  "t": {
    hint: "Little t: big line down, then a little line across.",
    strokes: [
      {
        d: "M 0.46,0.12 L 0.46,0.88",
        start: [0.46, 0.12],
        prompt: "Big line down",
      },
      {
        d: "M 0.28,0.42 L 0.64,0.42",
        start: [0.28, 0.42],
        prompt: "Little line across",
      },
    ],
  },
  "u": {
    hint: "Little u: line down, curve around, up, then a little line down.",
    strokes: [
      {
        d: "M 0.3,0.42 L 0.3,0.68 A 0.2 0.2 0 0 0 0.7,0.68 L 0.7,0.42",
        start: [0.3, 0.42],
        prompt: "Line down, curve around, and up",
      },
      {
        d: "M 0.7,0.42 L 0.7,0.88",
        start: [0.7, 0.42],
        prompt: "Little line down",
      },
    ],
  },
  "v": {
    hint: "Little v: slide down, then slide up.",
    strokes: [
      {
        d: "M 0.28,0.42 L 0.5,0.88 L 0.72,0.42",
        start: [0.28, 0.42],
        prompt: "Slide down, slide up",
      },
    ],
  },
  "w": {
    hint: "Little w: slide down, slide up, slide down, slide up.",
    strokes: [
      {
        d: "M 0.16,0.42 L 0.33,0.88 L 0.5,0.54 L 0.67,0.88 L 0.84,0.42",
        start: [0.16, 0.42],
        prompt: "Slide down, slide up, slide down, slide up",
      },
    ],
  },
  "x": {
    hint: "Little x: slide down one way, then slide down the other way.",
    strokes: [
      {
        d: "M 0.3,0.42 L 0.7,0.88",
        start: [0.3, 0.42],
        prompt: "Slide down to the right",
      },
      {
        d: "M 0.7,0.42 L 0.3,0.88",
        start: [0.7, 0.42],
        prompt: "Slide down to the left",
      },
    ],
  },
  "y": {
    hint: "Little y: slide down to the line, then a long slide down under the line.",
    strokes: [
      {
        d: "M 0.28,0.42 L 0.5,0.88",
        start: [0.28, 0.42],
        prompt: "Slide down to the right",
      },
      {
        d: "M 0.72,0.42 L 0.452,0.98",
        start: [0.72, 0.42],
        prompt: "Slide down to the left",
      },
    ],
  },
  "z": {
    hint: "Little z: line across, slide down, line across.",
    strokes: [
      {
        d: "M 0.3,0.42 L 0.7,0.42 L 0.3,0.88 L 0.7,0.88",
        start: [0.3, 0.42],
        prompt: "Line across, slide down, line across",
      },
    ],
  },
  "0": {
    hint: "Zero: start at the top and curve all the way around.",
    strokes: [
      {
        d: "M 0.418,0.143 A 0.24 0.38 0 1 0 0.654,0.791 A 0.24 0.38 0 0 0 0.418,0.143",
        start: [0.418, 0.143],
        prompt: "Curve all the way around",
      },
    ],
  },
  "1": {
    hint: "One: one big line down.",
    strokes: [
      {
        d: "M 0.5,0.12 L 0.5,0.88",
        start: [0.5, 0.12],
        prompt: "Big line down",
      },
    ],
  },
  "2": {
    hint: "Two: curve around, slide down, then a line across the bottom.",
    strokes: [
      {
        d: "M 0.28,0.3 C 0.28,0.08 0.72,0.08 0.72,0.32 C 0.72,0.48 0.52,0.6 0.28,0.88 L 0.72,0.88",
        start: [0.28, 0.3],
        prompt: "Curve around, slide down, line across",
      },
    ],
  },
  "3": {
    hint: "Three: curve around, then curve around again.",
    strokes: [
      {
        d: "M 0.28,0.22 C 0.34,0.1 0.72,0.08 0.72,0.3 C 0.72,0.46 0.56,0.5 0.46,0.5 C 0.58,0.5 0.74,0.54 0.74,0.7 C 0.74,0.92 0.34,0.92 0.26,0.78",
        start: [0.28, 0.22],
        prompt: "Curve around, curve around",
      },
    ],
  },
  "4": {
    hint: "Four: little line down, line across, then a big line down.",
    strokes: [
      {
        d: "M 0.3,0.12 L 0.3,0.58 L 0.76,0.58",
        start: [0.3, 0.12],
        prompt: "Little line down, line across",
      },
      {
        d: "M 0.64,0.12 L 0.64,0.88",
        start: [0.64, 0.12],
        prompt: "Big line down",
      },
    ],
  },
  "5": {
    hint: "Five: little line down, curve around, then a line across the top.",
    strokes: [
      {
        d: "M 0.32,0.12 L 0.3,0.46 C 0.44,0.38 0.74,0.4 0.74,0.64 C 0.74,0.9 0.4,0.92 0.26,0.78",
        start: [0.32, 0.12],
        prompt: "Little line down, curve around",
      },
      {
        d: "M 0.32,0.12 L 0.72,0.12",
        start: [0.32, 0.12],
        prompt: "Line across",
      },
    ],
  },
  "6": {
    hint: "Six: curve down, then curve all the way around at the bottom.",
    strokes: [
      {
        d: "M 0.7,0.16 C 0.62,0.06 0.3,0.18 0.27,0.66 A 0.23 0.22 0 1 0 0.716,0.585 A 0.23 0.22 0 0 0 0.27,0.66",
        start: [0.7, 0.16],
        prompt: "Curve down and around",
      },
    ],
  },
  "7": {
    hint: "Seven: line across, then slide down.",
    strokes: [
      {
        d: "M 0.26,0.12 L 0.74,0.12 L 0.42,0.88",
        start: [0.26, 0.12],
        prompt: "Line across, slide down",
      },
    ],
  },
  "8": {
    hint: "Eight: make an S, then curve back up to the top.",
    strokes: [
      {
        d: "M 0.653,0.188 A 0.2 0.19 0 1 0 0.5,0.5 A 0.22 0.19 0 0 1 0.5,0.88 A 0.22 0.19 0 0 1 0.5,0.5 A 0.2 0.19 0 0 0 0.653,0.188",
        start: [0.653, 0.188],
        prompt: "Make an S, then curve back up",
      },
    ],
  },
  "9": {
    hint: "Nine: little curve around at the top, then a big line down.",
    strokes: [
      {
        d: "M 0.72,0.34 A 0.22 0.22 0 1 0 0.293,0.415 A 0.22 0.22 0 0 0 0.72,0.34 L 0.72,0.88",
        start: [0.72, 0.34],
        prompt: "Curve around, big line down",
      },
    ],
  },
});

export function glyphStrokeCount(glyph) {
  const entry = GLYPH_STROKES[glyph];
  return entry ? entry.strokes.length : 0;
}

export default GLYPH_STROKES;
